import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import Copy from 'Src/components/Copy';
import { StatusIcon } from 'Src/components/icons/StatusIcon';
import TabButtons from 'Src/components/navigation/TabButtons';
import TabContent from 'Src/components/navigation/TabContent';
import { TabProvider } from 'Src/components/navigation/useTabs';
import { CheckDetailPrintJobsTab } from 'Src/feature/check-print/CheckDetailPrintJobsTab';
import { useCheckPrints } from 'Src/feature/check-print/useCheckPrints';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { routeParamValue } from 'Src/routes/params';
import useRoute from 'Src/routes/useRoute';

import { CheckActivityTab } from './CheckActivityTab';
import { Field, Form, useCheckInformationTab } from './useCheckInformationTab';
import { useCurrentCheck } from './useCurrentCheck';

import styles from './CheckDetailModal.module.scss';

const sectionId = 'CheckDetailModal';

interface CheckDetailModalProps {
  open: boolean;
  onClose: () => void;
}

const CheckDetailModal: FC<CheckDetailModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const currentCheck = useCurrentCheck();

  const { navigate, crumbs, route } = useRoute();
  const checkPrints = useCheckPrints({ checkId: currentCheck.query.data?.id });
  const checkInfoStore = useCheckInformationTab();

  const selectedTab = route.params.active.tabId;

  const handleBackdropClick = () => {
    // modal is on Check Info tab
    if (selectedTab === routeParamValue.checkInfo) {
      // form is unsaved -> cancel changes -> reset form and close
      checkInfoStore.form.action.reset();
      onClose();
      return;
    }
    // form is on a tab other than Check Info
    if (checkInfoStore.form.state.isValid) {
      // form is in a valid state and has auto-saved when the user switched tabs
      // -> can close without resetting the form
      onClose();
      return;
    }
    // form is on a tab other than Check Info AND form is not valid
    // -> user cannot close until they navigate back to Check Info tab
    return;
  };

  return (
    <Modal
      data-testid={sectionId}
      open={open}
      onBackDropClick={handleBackdropClick}
    >
      <ModalHeader className={styles.check_label_container}>
        <ModalTitle className={styles.check_label} copy={currentCheck.query.data?.label} />
      </ModalHeader>

      <TabProvider>
        <TabButtons
          routeParam="tabId"
          buttons={[
            { value: routeParamValue.checkInfo, label: t('check.information'), icon: checkInfoStore.form.state.isValid ? undefined : <StatusIcon color="error" /> },
            { value: routeParamValue.checkGuestInfo, label: t('check.labels.guestInfo') },
            { value: routeParamValue.checkPrintJobs, label: t('printer.labels.printJobs'), icon: checkPrints.all.state.counts['Failed'] > 0 ? <StatusIcon color="error" /> : undefined },
            { value: routeParamValue.checkActivity, label: t('check.activity') },
          ]}
          variant="underline"
          className={styles.tabs}
          onChange={(tabId) => {
            if (tabId === routeParamValue.checkInfo && checkInfoStore.form.state.isDirty) {
              checkInfoStore.onUpdateCheckInfo();
            }
            navigate(crumbs.pos.check.detail.interpolate({
              checkId: currentCheck.query.data?.id,
              tabId,
              itemSelect: route.params.active.itemSelect,
            }));
          }}
        />
        <TabContent value={routeParamValue.checkInfo}>
          <ModalContent>
            <Form usedForm={checkInfoStore.form} omitChangeTracking>
              <Field type="dropdownList" name="orderTypeId" label={t('orderType.label')} options={checkInfoStore.options.orderType} fullWidth />
              <Field type="numberInput" name="partySize" label={t('labels.partySize')} fullWidth expandable />
              <Field
                type="text"
                name="labelPrefix"
                label={t('check.labels.labelPrefix')}
                fullWidth
                adornment={{ end: <Copy>{checkInfoStore.check?.number}</Copy> }}
                variant="underline"
              />
            </Form>
          </ModalContent>
        </TabContent>

        <TabContent value={routeParamValue.checkPrintJobs}>
          {/* this view has the action buttons and modal content internally */}
          <CheckDetailPrintJobsTab onClose={onClose} disableDoneAction={!checkInfoStore.form.state.isValid} />
        </TabContent>

        <TabContent value={routeParamValue.checkActivity}>
          <ModalContent>
            <CheckActivityTab />
          </ModalContent>
        </TabContent>

        <TabContent value={routeParamValue.checkGuestInfo}>
          <ModalContent>
            <Copy>Coming Soon</Copy>
          </ModalContent>
        </TabContent>

        {/* these views share the same action button */}
        <TabContent value={[routeParamValue.checkInfo, routeParamValue.checkGuestInfo, routeParamValue.checkActivity]}>
          <ModalActions>
            <ModalAction
              color="secondary"
              label={t('labels.done')}
              onClick={async () => {
                if (selectedTab === routeParamValue.checkInfo && checkInfoStore.form.state.isDirty) {
                  checkInfoStore.onUpdateCheckInfo();
                }
                onClose();
              }}
              disabled={!checkInfoStore.form.state.isValid}
            />
          </ModalActions>
        </TabContent>

      </TabProvider>
    </Modal>
  );
};

export { CheckDetailModal, CheckDetailModalProps };
